// Replaces the Liquid collection fallback in snippets/cart-upsell.liquid with Search & Discovery recommendations.
(function () {
  'use strict';

  if (customElements.get('gp-cart-upsell')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const ENDPOINT = `${root.replace(/\/+$/, '')}/recommendations/products.json`;

  // Module-level so it survives the drawer's innerHTML swap on every cart mutation.
  const cache = new Map();

  // Over-fetch (endpoint max) because results are filtered after merging.
  const PER_LINE = 10;

  function fetchFor(productId, intent) {
    const key = `${intent}:${productId}`;
    if (cache.has(key)) return cache.get(key);

    const url = `${ENDPOINT}?product_id=${encodeURIComponent(productId)}&limit=${PER_LINE}&intent=${encodeURIComponent(intent)}`;

    const request = fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => (body && Array.isArray(body.products) ? body.products : []))
      .catch((error) => {
        // Resolve empty so the collection fallback stays.
        console.warn('[gp-upsell] recommendations failed', error);
        return [];
      });

    cache.set(key, request);
    return request;
  }

  const inList = (list, id) => !!list && list.indexOf(`,${id},`) !== -1;

  const purchasable = (product) => !!product && product.available !== false && Array.isArray(product.variants);

  // Tags that keep a product out of the strip; must match HIDDEN_TAGS in cart-upsell.liquid.
  const HIDDEN_TAGS = ['hide-from-cart-upsell', 'mws_fee_generated'];
  const hidden = (product) => (product.tags || []).some((tag) => HIDDEN_TAGS.indexOf(tag) !== -1);

  // Cheapest available variant, ties to the first listed; must match cart-upsell.liquid.
  function variantOf(product) {
    return (product.variants || []).reduce((best, v) => {
      if (!v || !v.available) return best;
      return !best || pence(v.price) < pence(best.price) ? v : best;
    }, null);
  }

  function variantLabel(product, variant) {
    if (!variant || (product.variants || []).length < 2) return '';
    return variant.public_title || variant.title || '';
  }

  // This endpoint returns integer pence (unlike /products.json); don't multiply by 100.
  const pence = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number) : 0;
  };

  function selectProducts(lists, options) {
    const opts = options || {};
    const inCart = opts.inCart || '';
    const limit = Number(opts.limit) || 8;

    const seen = new Set();
    const merged = [];

    lists.forEach((list) => {
      (list || []).forEach((product) => {
        if (!product || seen.has(product.id)) return;
        seen.add(product.id);
        merged.push(product);
      });
    });

    return merged
      .filter((product) => purchasable(product))
      .filter((product) => !hidden(product))
      .filter((product) => !inList(inCart, product.id))
      .filter((product) => !!variantOf(product))
      .map((product) => ({ product, price: pence(variantOf(product).price) }))
      .sort((a, b) => a.price - b.price)
      .slice(0, limit)
      .map((entry) => entry.product);
  }

  function money(amountPence, format) {
    const amount = (amountPence / 100).toFixed(2);
    if (format && /\{\{\s*amount\s*\}\}/.test(format)) {
      return format.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(amountPence / 100);
    } catch (error) {
      return `£${amount}`;
    }
  }

  const thumbnail = (src) => `${src}${String(src).indexOf('?') === -1 ? '?' : '&'}width=160`;

  const escapeHTML = (value) =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );

  class GPCartUpsell extends HTMLElement {
    connectedCallback() {
      // Read from Liquid's attribute, not cartState, so it matches the rendered lines.
      this.inCart = this.getAttribute('data-gp-upsell-in-cart') || '';
      this.limit = Number(this.getAttribute('data-gp-upsell-limit')) || 8;

      const requested = this.getAttribute('data-gp-upsell-intent');
      const intent = requested === 'complementary' ? 'complementary' : 'related';

      const ids = this.inCart.split(',').filter(Boolean);
      if (!ids.length) return;

      // _ready is awaited by tools/cart-harness.mjs.
      this._ready = Promise.all(ids.map((id) => fetchFor(id, intent))).then((lists) => {
        if (!this.isConnected) return;
        this.render(selectProducts(lists, { inCart: this.inCart, limit: this.limit }));
      });
    }

    render(products) {
      if (!products.length) return;

      const track = this.querySelector('[data-gp-upsell-track]');
      if (!track) return;

      track.innerHTML = products.map((product) => this.card(product)).join('');
      track.setAttribute('data-gp-upsell-source', 'recommendations');
      this.classList.remove('gp-cart-upsell-host--empty');

      const drawer = this.closest('gp-cart-drawer');
      if (drawer && typeof drawer._syncUpsell === 'function') drawer._syncUpsell();
    }

    // Must match the card markup in snippets/cart-upsell.liquid.
    card(product) {
      const variant = variantOf(product);
      if (!variant) return '';

      const format = this.getAttribute('data-gp-upsell-money-format') || '';
      const price = pence(variant.price);
      const was = pence(variant.compare_at_price);

      const addLabel = this.getAttribute('data-gp-upsell-add-label') || 'Add';
      const label = variantLabel(product, variant);

      const image = product.featured_image || (product.images && product.images[0]) || '';
      const url = product.url || `/products/${product.handle}`;

      const media = image
        ? `<a class="gp-cart-upsell-card__media" href="${escapeHTML(url)}" tabindex="-1">
             <img class="gp-cart-upsell-card__image" src="${escapeHTML(thumbnail(image))}" alt="" loading="lazy" width="48" height="48">
           </a>`
        : '';

      const strike =
        was > price ? `<s class="gp-cart-upsell-card__was">${escapeHTML(money(was, format))}</s>` : '';

      const variantName = label
        ? `<span class="gp-cart-upsell-card__variant" title="${escapeHTML(label)}"><span class="gp-cart-upsell-card__variant-text">${escapeHTML(label)}</span></span>`
        : '';

      // type="button": inside #gp-CartDrawer-Form, whose default submit goes to checkout.
      const action = `<button type="button" class="gp-cart-upsell-card__add" data-gp-upsell-add data-gp-upsell-variant="${escapeHTML(variant.id)}">${escapeHTML(addLabel)}</button>`;

      return `<li class="gp-cart-upsell-card">
          ${media}
          <a class="gp-cart-upsell-card__title" href="${escapeHTML(url)}">${escapeHTML(product.title)}</a>
          <span class="gp-cart-upsell-card__prices">
            ${variantName}${strike}<span class="gp-cart-upsell-card__price">${escapeHTML(money(price, format))}</span>
          </span>
          ${action}
          <div class="gp-cart-upsell-card__error" role="alert" data-gp-line-error></div>
        </li>`;
    }
  }

  customElements.define('gp-cart-upsell', GPCartUpsell);

  window.gpUpsellRecommendations = { selectProducts, variantOf, money, cache };
})();
